import { IScene } from "@agenteresolve/types"
import React from "react"
import {ContextMenuSceneRequest, ContextMenuTimelineRequest, DesignType, IDesign} from "~/interfaces/DesignEditor"
import type { ContentKind } from "~/constants/content-kinds"
import type { DesignFormat } from "~/constants/formats"

interface ISceneEditorContext {
  scenes: IScene[]
  setScenes: (value: ((prevState: IScene[]) => IScene[]) | IScene[]) => void
  currentScene: IScene | null
  setCurrentScene: React.Dispatch<React.SetStateAction<IScene | null>>
  currentDesign: IDesign
  setCurrentDesign: React.Dispatch<React.SetStateAction<IDesign>>
  isSidebarOpen: boolean
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
  editorType: DesignType
  setEditorType: React.Dispatch<React.SetStateAction<DesignType>>
  contentKind: ContentKind
  setContentKind: React.Dispatch<React.SetStateAction<ContentKind>>
  activeFormat: DesignFormat | null
  setActiveFormat: React.Dispatch<React.SetStateAction<DesignFormat | null>>
  displayPreview: boolean
  setDisplayPreview: React.Dispatch<React.SetStateAction<boolean>>
  currentPreview: string
  setCurrentPreview: React.Dispatch<React.SetStateAction<string>>
  displayAnimation: boolean
  setDisplayAnimation: React.Dispatch<React.SetStateAction<boolean>>
  animationPreset: string
  setAnimationPreset: React.Dispatch<React.SetStateAction<string>>
  animationPlaying: boolean
  setAnimationPlaying: React.Dispatch<React.SetStateAction<boolean>>
  maxTime: number
  setMaxTime: React.Dispatch<React.SetStateAction<number>>
  contextMenuTimelineRequest: ContextMenuTimelineRequest
  setContextMenuTimelineRequest: React.Dispatch<React.SetStateAction<ContextMenuTimelineRequest>>
  contextMenuSceneRequest: ContextMenuTimelineRequest
  setContextMenuSceneRequest: React.Dispatch<React.SetStateAction<ContextMenuTimelineRequest>>
}

export const DesignEditorContext = React.createContext<ISceneEditorContext>({
  scenes: [],
  setScenes: () => {},
  currentScene: null,
  setCurrentScene: () => {},
  currentDesign: {
    id: "",
    frame: {
      width: 1,
      height: 1,
    },
    metadata: {},
    name: "",
    preview: "",
    scenes: [],
    type: "",
  },
  setCurrentDesign: () => {},
  isSidebarOpen: true,
  setIsSidebarOpen: () => {},
  editorType: "NONE",
  setEditorType: () => {},
  contentKind: "DIGITAL",
  setContentKind: () => {},
  activeFormat: null,
  setActiveFormat: () => {},

  displayPreview: false,
  setDisplayPreview: () => {},
  currentPreview: "",
  setCurrentPreview: () => {},
  displayAnimation: false,
  setDisplayAnimation: () => {},
  animationPreset: "slide-up",
  setAnimationPreset: () => {},
  animationPlaying: false,
  setAnimationPlaying: () => {},
  maxTime: 0,
  setMaxTime: () => {},
  contextMenuTimelineRequest: {
    id: "",
    left: 0,
    top: 0,
    visible: false,
  },
  setContextMenuTimelineRequest: () => {},
  contextMenuSceneRequest: {
    id: "",
    left: 0,
    top: 0,
    visible: false,
  },
  setContextMenuSceneRequest: () => {},

})

export const DesignEditorProvider = ({ children }: { children: React.ReactNode }) => {
  const [scenes, setScenes] = React.useState<IScene[]>([])
  const [currentScene, setCurrentScene] = React.useState<IScene | null>(null)
  const [currentDesign, setCurrentDesign] = React.useState<IDesign>({
    id: "",
    frame: {
      width: 1,
      height: 1,
    },
    metadata: {},
    name: "",
    preview: "",
    scenes: [],
    type: "",
  })
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true)
  const [editorType, setEditorType] = React.useState<DesignType>("NONE")
  const [contentKind, setContentKind] = React.useState<ContentKind>("DIGITAL")
  const [activeFormat, setActiveFormat] = React.useState<DesignFormat | null>(null)
  const [displayPreview, setDisplayPreview] = React.useState<boolean>(false)
  const [currentPreview, setCurrentPreview] = React.useState<string>("")
  const [displayAnimation, setDisplayAnimation] = React.useState<boolean>(false)
  const [animationPreset, setAnimationPreset] = React.useState<string>("slide-up")
  const [animationPlaying, setAnimationPlaying] = React.useState<boolean>(false)
  const [maxTime, setMaxTime] = React.useState(5000)
  const [contextMenuTimelineRequest, setContextMenuTimelineRequest] = React.useState<ContextMenuTimelineRequest>({
    id: "",
    left: 0,
    top: 0,
    visible: false,
  })
  const [contextMenuSceneRequest, setContextMenuSceneRequest] = React.useState<ContextMenuSceneRequest>({
    id: "",
    left: 0,
    top: 0,
    visible: false,
  })
  const context = {
    scenes,
    setScenes,
    currentScene,
    setCurrentScene,
    currentDesign,
    setCurrentDesign,
    isSidebarOpen,
    setIsSidebarOpen,
    editorType,
    setEditorType,
    contentKind,
    setContentKind,
    activeFormat,
    setActiveFormat,
    displayPreview,
    setDisplayPreview,
    currentPreview,
    setCurrentPreview,
    displayAnimation,
    setDisplayAnimation,
    animationPreset,
    setAnimationPreset,
    animationPlaying,
    setAnimationPlaying,
    maxTime,
    setMaxTime,
    contextMenuTimelineRequest,
    setContextMenuTimelineRequest,
    contextMenuSceneRequest,
    setContextMenuSceneRequest,
  }
  return <DesignEditorContext.Provider value={context}>{children}</DesignEditorContext.Provider>
}
